import { CrmFieldValue } from 'app/main/apps/crm/types';
import { SegmentPresetKey } from 'app/main/apps/listCategory/type';
import {
  FlatLoyaltyOfferDTO,
  LoyaltyCardInDTO,
  LoyaltyCardTransaction,
  LoyaltyOfferDTO,
  PercentageLoyaltyOfferDTO,
  ProductLoyaltyOfferDTO,
} from 'app/main/apps/loyalties/type';
import { CouponUserItemInDTO } from 'app/main/design-system/models/coupon.type';

export interface ClientEntity
  extends Pick<LoyaltyCardInDTO, 'points' | 'totalCollectedPoints' | 'averageBasket' | 'weeklyAverageBasket'> {
  id: string;
  loyaltyCardId: string | null;
  isPartialClient?: boolean;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phoneNumber: PhoneNumber | null;
  clientType: ClientType | null;
  acquiredFrom: AcquiredFromTypeEnum[];
  nbOffersRedeemed: number;
  totalTransactions: number;
  lastTagScan: string | null;
  lastVisit: string | null;
  hasMobileActivity: boolean;
  userFieldsValues?: FieldValueDTO[] | null;
  segments?: ClientSegmentBadge[] | null;
  latestFormAnswers?: ClientFormAnswer[];
  nbFormResponses?: number;
}

/** One category a client falls into, as the clients list reports it. */
export interface ClientSegmentBadge {
  id: string | null;
  presetKey: SegmentPresetKey | null;
  label: string | null;
  emoji: string | null;
  color: string | null;
}

export enum FormElementType {
  MULTIPLE_CHOICE = 'MULTIPLE_CHOICE',
  SINGLE_CHOICE = 'SINGLE_CHOICE',
  SHORT_TEXT = 'SHORT_TEXT',
  LONG_TEXT = 'LONG_TEXT',
  SELECT = 'SELECT',
  NUMBER = 'NUMBER',
  EMAIL = 'EMAIL',
  DATETIME = 'DATETIME',
  STAR_RATING = 'STAR_RATING',
  FILE_UPLOAD = 'FILE_UPLOAD',
  IMAGE = 'IMAGE',
  PARAGRAPH = 'PARAGRAPH',
  CRM_JOURNEY = 'CRM_JOURNEY',
}

export type FormAnswerValue = string | number | string[] | null;

export interface FormQuestionColumn {
  formId: string;
  formName: string;
  elementId: string;
  label: string;
  type: FormElementType;
  order: number;
  /** Clients-list column id, as stored in the column preferences. */
  columnId: string;
}

export interface ClientFormAnswer {
  formId: string;
  elementId: string;
  type: FormElementType;
  value: FormAnswerValue;
  answeredAt: string;
}

export interface ClientFormResponseAnswer {
  elementId: string;
  label: string;
  type: FormElementType;
  value: FormAnswerValue;
}

export interface ClientFormResponse {
  id: string;
  formId: string;
  formName: string;
  storeId: string;
  storeName: string;
  submittedAt: string;
  source?: FormResponseSource;
  answers: ClientFormResponseAnswer[];
}

export enum FormResponseSource {
  PUBLIC = 'PUBLIC',
  MERCHANT = 'MERCHANT',
}

export interface ClientEntityListMetadata {
  total: number;
}

export interface ClientEntityList {
  data: ClientEntity[];
  metadata: ClientEntityListMetadata;
  hiddenPartialClients?: number;
}

export interface ClientLoyalty {
  points: number;
  offers: (FlatLoyaltyOfferDTO | PercentageLoyaltyOfferDTO | ProductLoyaltyOfferDTO | LoyaltyOfferDTO)[];
}

export interface ClientTopProduct {
  posProductId: string | null;
  name: string;
  quantity: number;
  priceUnit: number;
  lastBuy: string;
}

export interface FieldValueDTO {
  id: string;
  fieldId: string;
  label: string;
  value: CrmFieldValue;
}

export interface UpsertedCrmFieldsDTO {
  values: FieldValueDTO[];
  /** Points the save granted, null when it granted none. */
  creditedPoints: number | null;
}

export interface ClientDetails extends ClientEntity {
  gender: string;
  dateOfBirth: string;
  zipOrPostcode: string;
  confirmedPhoneNumbers?: PhoneNumber[];
  loyaltyCardTransactions: LoyaltyCardTransaction[];
  loyaltyOffers: (FlatLoyaltyOfferDTO | PercentageLoyaltyOfferDTO | ProductLoyaltyOfferDTO | LoyaltyOfferDTO)[];
  activeCoupons: CouponUserItemInDTO[];
  hasConfirmedAccount: boolean;
  userFieldsValues?: FieldValueDTO[];
  loyaltyCardId: string;
  note?: string;
  isFromMigration?: boolean;
}

export interface ClientCreditPoints {
  userId: string;
  merchantId: string;
  storeId: string;
  points: number;
}

export const CLIENT_RECORDS = ['CLIENT', 'PARTIAL'] as const;
export type ClientRecord = (typeof CLIENT_RECORDS)[number];

export const isClientRecord = (value: string | null): value is ClientRecord =>
  CLIENT_RECORDS.some(record => record === value);

export const ACTIVITY_PERIOD_UNITS = ['DAYS', 'MONTHS'] as const;
export type ActivityPeriodUnit = (typeof ACTIVITY_PERIOD_UNITS)[number];

export const isActivityPeriodUnit = (value: string | null): value is ActivityPeriodUnit =>
  ACTIVITY_PERIOD_UNITS.some(unit => unit === value);

/** Days are YYYY-MM-DD in the franchise calendar, both included; the API resolves the bounds. */
export type ActivityPeriod =
  | { mode: 'range'; from: string; to: string }
  | { mode: 'last'; count: number; unit: ActivityPeriodUnit };

/** The period as query parameters: one spelling for the request and the shareable URL. */
export const activityPeriodParams = (period: ActivityPeriod): [ClientsFiltersUrlEnum, string][] =>
  period.mode === 'range'
    ? [
        [ClientsFiltersUrlEnum.activityFrom, period.from],
        [ClientsFiltersUrlEnum.activityTo, period.to],
      ]
    : [
        [ClientsFiltersUrlEnum.activityLast, String(period.count)],
        [ClientsFiltersUrlEnum.activityLastUnit, period.unit],
      ];

export enum ClientType {
  lost = 'LOST_CUSTOMER',
  new = 'NEW_CUSTOMER',
  regular = 'REGULAR_CUSTOMER',
}

interface PhoneNumber {
  nationalNumber: string;
  countryCode: string;
  regionCode: string;
  e164Number: string;
  stopSMS?: boolean;
}

export interface Country {
  name: string;
  dialCode: string;
  emoji: string;
  isSMSSupported?: boolean;
}

export type ClientSearchedType = 'email' | 'phoneNumber';

export interface ClientSearched {
  id: string | null;
  firstName: string | null;
  lastName: string | null;
  zipOrPostcode: string | null;
  dateOfBirth: string | null;
  note: string | null;
  hasConfirmedAccount: boolean;
  isFromMigration: boolean;
}

export interface ClientPhoneNumberDialogFields {
  searchPhoneNumber: string;
  dialCode: string;
  selectedValue: number;
}

export type ClientPhoneNumberParts = Pick<ClientPhoneNumberDialogFields, 'dialCode' | 'searchPhoneNumber'>;

export interface ClientAddUserDialogFields extends ClientPhoneNumberDialogFields {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  points: number;
  zipOrPostcode: string;
  note: string;
}

export interface ClientEditUserDialogFields extends Omit<ClientAddUserDialogFields, 'points' | 'searchPhoneNumber'> {
  phoneNumber: string;
  email: string;
}
export interface AddClientDTO {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  dateOfBirth: string | null;
  zipOrPostcode: string;
  points: number;
  storeId: string;
  merchantId: string;
  note: string | null;
}

export interface AddClientInDTO extends AddClientDTO {
  id: string;
}

export interface EditClientDTO extends Partial<Omit<AddClientDTO, 'storeId' | 'points' | 'merchantId'>> {
  email?: string;
}

export enum ClientsSortTypeEnum {
  lastName = 'lastName',
  clientType = 'clientType',
  contact = 'contact',
  lastVisit = 'lastVisit',
  nbVisits = 'nbVisits',
  averageBasket = 'averageBasket',
  weeklyAverageBasket = 'weeklyAverageBasket',
  nbPoints = 'nbPoints',
  totalCollectedPoints = 'totalCollectedPoints',
  redeemOffers = 'redeemOffers',
  acquiredFrom = 'acquiredFrom',
}

export interface ColumnPreference {
  columnId: string;
  visible: boolean;
}

export interface ColumnPreferences {
  screen: string;
  columns: ColumnPreference[];
}

/** A CRM field column, ordered on the value each client holds for that field. */
export type CrmSortKey = `crm:${string}`;

export type ClientsSortKey = ClientsSortTypeEnum | CrmSortKey;

export interface CrmFilterValue {
  selected?: string[];
  text?: string;
  boolean?: boolean;
  min?: number;
  max?: number;
  minDate?: string;
  maxDate?: string;
}

export interface CrmFilter {
  fieldId: string;
  value: CrmFilterValue;
}

export const hasCrmFilterValue = (value: CrmFilterValue) =>
  Object.values(value).some(
    entry => entry !== undefined && entry !== '' && !(Array.isArray(entry) && entry.length === 0),
  );

/**
 * The criterion as query parameters, for the request and for the shareable URL.
 *
 * One list for both: a link pasted back into the browser is read by
 * crmFilterFromUrl and becomes the very filter the request carries, so the two
 * spellings drifting apart would make a shared link filter differently from the
 * screen it was copied from.
 */
export const crmFilterParams = (crmFilter: CrmFilter): [ClientsFiltersUrlEnum, string][] => {
  if (!hasCrmFilterValue(crmFilter.value)) return [];

  const { selected, text, boolean, min, max, minDate, maxDate } = crmFilter.value;
  const singles: [ClientsFiltersUrlEnum, string | number | boolean | undefined][] = [
    [ClientsFiltersUrlEnum.crmText, text],
    [ClientsFiltersUrlEnum.crmBool, boolean],
    [ClientsFiltersUrlEnum.crmMin, min],
    [ClientsFiltersUrlEnum.crmMax, max],
    [ClientsFiltersUrlEnum.crmMinDate, minDate],
    [ClientsFiltersUrlEnum.crmMaxDate, maxDate],
  ];

  return [
    [ClientsFiltersUrlEnum.crmFieldId, crmFilter.fieldId],
    ...(selected ?? []).map(option => [ClientsFiltersUrlEnum.crmSelected, option] as [ClientsFiltersUrlEnum, string]),
    ...singles
      .filter(([, value]) => value !== undefined && value !== '')
      .map(([key, value]) => [key, String(value)] as [ClientsFiltersUrlEnum, string]),
  ];
};

export enum ClientsFiltersUrlEnum {
  crmFieldId = 'crmFieldId',
  crmSelected = 'crmSelected',
  crmText = 'crmText',
  crmBool = 'crmBool',
  crmMin = 'crmMin',
  crmMax = 'crmMax',
  crmMinDate = 'crmMinDate',
  crmMaxDate = 'crmMaxDate',
  storeIds = 'storeIds',
  acquiredFrom = 'acquiredFrom',
  clientType = 'clientType',
  segmentRefs = 'segmentRefs',
  record = 'record',
  activityFrom = 'activityFrom',
  activityTo = 'activityTo',
  activityLast = 'activityLast',
  activityLastUnit = 'activityLastUnit',
  search = 'search',
  store = 'store',
  page = 'page',
  rowsPerPage = 'rowsPerPage',
}

export enum ClientsSortsUrlEnum {
  sort = 'sort',
  order = 'order',
}

export enum ClientsSortOrderEnum {
  ascending = 'asc',
  descending = 'desc',
}
export interface LoyaltyCardTransactions {
  date: string;
  type: string;
  points: string;
}

export enum AcquiredFromTypeEnum {
  migration = 'MIGRATION',
  emarketing = 'EMARKETING',
  store = 'STORE',
  game = 'GAME',
  referral = 'REFERRAL',
}

export interface Taxe {
  rate: number;
  value: number;
}
export interface ReceiptItem {
  name: string;
  unitPrice: number;
  quantity: number;
  taxes: {
    rate: number;
  };
}
export interface EtpReceiptDTO {
  id: string;
  storeId: string;
  amount: number;
  billedAt: string;
  points: number;
  initialPoints: number;
  multiplier: number;
}

export interface ReceiptDetail {
  storeName: string;
  storeLogo?: string;
  itemsSum?: number;
  points: number;
  multiplier?: number;
  billDate?: string;
  itemsSumHT?: number;
  receiptItems?: ReceiptItem[];
  taxes?: Taxe[];
  initialPoints?: number;
}
